import { useSetPageTitle } from "libs/ga"
import Page from "components/Common/Page"
import {
  CaloriesInputBox,
  CaloriesInputHint,
  CustomizeGoalSubtitle,
  CustomizeGoalTitle,
  CustomizeGoalWrapper,
  Divider,
} from "./styled"
import { useNavigate } from "react-router-dom"
import PATHS from "router/paths"
import Button from "components/Buttons/Button"
import { FlexBox } from "components/Common/FlexBox"
import NumericInput from "components/inputs/NumericInput"
import { Fragment, useState } from "react"
import SwitchSelector from "components/inputs/SwitchSelector"
import MacroGramInput from "./MarcoGramInput"
import { useGoal } from "features/onboarding/hooks"
import MarcoCaloriesPercentInput from "./MarcoCaloriesPercentInput"
import useManualEditGoalHandlers from "features/profile/hooks/manualEditGoal/useManualEditGoalHandlers"
import { useManualUpdateGoal } from "features/profile/hooks/manualEditGoal/useManualEditGoal"
import AppStyleCtaBox from "components/Common/AppStyleCtaBox"

enum IEditMode {
  Percentage = "Percentage",
  Grams = "Grams",
}
const editModeOptions = [
  {
    label: "Percentage",
    value: IEditMode.Percentage,
  },
  {
    label: "Grams",
    value: IEditMode.Grams,
  },
]

const ManualEditGoal = () => {
  useSetPageTitle("Manual Edit Goal | Profile")
  const { goal, getGoalLoading } = useGoal()
  const navigate = useNavigate()
  const onBack = () => {
    navigate(PATHS.app.yourGoal.root)
  }

  const [editMode, setEditMode] = useState(IEditMode.Grams)

  const {
    calories,
    proteinGram,
    fatGram,
    carbsGram,
    proteinCaloriesPercent,
    fatCaloriesPercent,
    carbsCaloriesPercent,
    isCaloriesError,
    isProteinGramError,
    onCaloriesChange,
    onProteinGramChange,
    onFatGramChange,
    onCarbsGramChange,
    onProteinFatCarbsPercentAndGramChange,
  } = useManualEditGoalHandlers(goal)

  const { onUpdateGoalManually, manualUpdateGoalLoading } =
    useManualUpdateGoal()

  const handleSubmit = () => {
    onUpdateGoalManually({
      caloriePerDay: calories ?? 0,
      pfcBalance: {
        protein: proteinCaloriesPercent / 100,
        fat: fatCaloriesPercent / 100,
        carb: carbsCaloriesPercent / 100,
      },
      pfc: {
        protein: proteinGram ?? 0,
        fat: fatGram ?? 0,
        carb: carbsGram ?? 0,
      },
    })
  }

  return (
    <Page onBack={onBack} theme="dark" isAppPaddingStyle>
      <CustomizeGoalWrapper>
        <FlexBox $direction="column" $gap={4} style={{ padding: "4px 0" }}>
          <CustomizeGoalTitle>My Daily Macros</CustomizeGoalTitle>
          <CustomizeGoalSubtitle>Customize your plan</CustomizeGoalSubtitle>
        </FlexBox>

        <div>
          <CaloriesInputBox>
            <NumericInput
              label="Calories"
              detailTrailing="cal"
              value={calories}
              onChange={onCaloriesChange}
              isError={isCaloriesError}
              min={0}
              max={100000}
              disabled={getGoalLoading}
            />
            <CaloriesInputHint $isError={isCaloriesError}>
              Calorie must be more than 0 and less than 10,000
            </CaloriesInputHint>
          </CaloriesInputBox>

          <Divider />

          <FlexBox $justify="center" $alignItems="center">
            <SwitchSelector
              options={[editModeOptions[0], editModeOptions[1]]}
              value={editMode}
              onChange={(val) => setEditMode(val as IEditMode)}
            />
          </FlexBox>
          <FlexBox $direction="column" $gap={16} style={{ marginTop: 24 }}>
            {editMode === IEditMode.Grams ? (
              <Fragment>
                <MacroGramInput
                  value={proteinGram}
                  onChange={onProteinGramChange}
                  label="Protein"
                  detailTrailing="g"
                  percent={proteinCaloriesPercent}
                  disabled={getGoalLoading}
                  isError={isProteinGramError}
                />
                <MacroGramInput
                  value={fatGram}
                  onChange={onFatGramChange}
                  label="Fat"
                  detailTrailing="g"
                  percent={fatCaloriesPercent}
                  disabled={getGoalLoading}
                />
                <MacroGramInput
                  value={carbsGram}
                  onChange={onCarbsGramChange}
                  label="Carbs"
                  detailTrailing="g"
                  percent={carbsCaloriesPercent}
                  disabled={getGoalLoading}
                />
              </Fragment>
            ) : (
              <Fragment>
                <MarcoCaloriesPercentInput
                  value={proteinCaloriesPercent}
                  onChange={onProteinFatCarbsPercentAndGramChange}
                  label="Protein"
                  detailTrailing="%"
                  gram={proteinGram}
                  disabled={getGoalLoading}
                  isError={isProteinGramError}
                  proteinGram={proteinGram}
                  fatGram={fatGram}
                  carbsGram={carbsGram}
                  proteinCaloriesPercent={proteinCaloriesPercent}
                  fatCaloriesPercent={fatCaloriesPercent}
                  carbsCaloriesPercent={carbsCaloriesPercent}
                  calories={calories}
                />
                <MarcoCaloriesPercentInput
                  value={fatCaloriesPercent}
                  onChange={onProteinFatCarbsPercentAndGramChange}
                  label="Fat"
                  detailTrailing="%"
                  gram={fatGram}
                  disabled={getGoalLoading}
                  proteinGram={proteinGram}
                  fatGram={fatGram}
                  carbsGram={carbsGram}
                  proteinCaloriesPercent={proteinCaloriesPercent}
                  fatCaloriesPercent={fatCaloriesPercent}
                  carbsCaloriesPercent={carbsCaloriesPercent}
                  calories={calories}
                />
                <MarcoCaloriesPercentInput
                  value={carbsCaloriesPercent}
                  onChange={onProteinFatCarbsPercentAndGramChange}
                  label="Carbs"
                  detailTrailing="%"
                  gram={carbsGram}
                  disabled={getGoalLoading}
                  proteinGram={proteinGram}
                  fatGram={fatGram}
                  carbsGram={carbsGram}
                  proteinCaloriesPercent={proteinCaloriesPercent}
                  fatCaloriesPercent={fatCaloriesPercent}
                  carbsCaloriesPercent={carbsCaloriesPercent}
                  calories={calories}
                />
              </Fragment>
            )}
          </FlexBox>
        </div>
      </CustomizeGoalWrapper>

      <AppStyleCtaBox>
        <Button
          style={{ width: "100%" }}
          $theme="dark"
          onClick={handleSubmit}
          loading={manualUpdateGoalLoading}
          disabled={getGoalLoading || !goal || isCaloriesError}
          $borderLess
          $type="primary"
        >
          Save changes
        </Button>
      </AppStyleCtaBox>
    </Page>
  )
}

export default ManualEditGoal
